<template>
  <div class="calendar-month">
    <div class="calendar-month__header">
      {{monthLabel}}
    </div>
    <div class="calendar-month__days">
      <div v-for="day in weekdaysLabels" :key="day" class="calendar-month__days-label">
        {{day}}
      </div>
    </div>
    <div v-for="(week, weekIndex) in weeksList" :key="weekIndex" class="calendar-month__week" :class="{'calendar-month__week--first': weekIndex === 0}">
      <div
        v-for="date in week"
        :key="date"
        class="week-day"
        :class="{
          // today: date.isToday,
          selected: date.isSelected,
          highlighted: date.isHighlighted,
          'start-date': date.isStartDate,
          'end-date': date.isEndDate,
          'disabled': date.isInPast || !checkIsAvailable(date.value),
        }"
      >
        <button v-if="date.value" :disabled="date.isInPast || !checkIsAvailable(date.value)" @click="onClick(date)">
          {{date.label}}
          <span v-if="props.price && !date.isInPast" class="price">{{props.price}}</span>
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import dayjs from '~/utils/dayjs'

const props = defineProps({
  monthDate: {type: String, required: true},
  from: {type: Number, default: 0},
  to: {type: Number, default: 0},
  price: {type: String, default: ''},
  availableDates: {type: Array, default: []},
  filterDates: {type: Boolean, default: false}
})

const datesList = computed(() => props.availableDates)

const emit = defineEmits(['date-click'])

const weekdaysLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const currentDate = dayjs().startOf('date')
const currentMonthStart = dayjs(props.monthDate, 'YYYY-MM-DD').startOf('month')
const monthLabel = currentMonthStart.format('MMMM YYYY')
const dates = [] as any[]
const daysAmount = currentMonthStart.daysInMonth()
const weeksList = ref([[]] as any[])

function processDate(date: any) {
  date.isSelected = date.unix === props.from || date.unix === props.to
  date.isHighlighted = (props.from && date.unix >= props.from) && (props.to && date.unix <= props.to)
  date.isStartDate = props.from && props.to && (date.unix === props.from)
  date.isEndDate = props.from && props.to && (date.unix === props.to)
  return date
}

function fillDates() {
  for (let i = 0; i < daysAmount; i++) {
    const dayjsDate = currentMonthStart.add(i, 'day')
    const unix = dayjsDate.unix()
    const processedDate = {
      value: dayjsDate.format('YYYY-MM-DD'),
      unix: unix,
      label: dayjsDate.format('DD'),
      dayIndex: dayjsDate.day(),
      isToday: dayjsDate.isSame(currentDate, 'date'),
      isInPast: unix < currentDate.unix()
    }
    dates.push(processDate(processedDate))
  }
}

function fillWeeks() {
  const firstDateDayIndex = dates[0].dayIndex
  for (let i = 0; i < firstDateDayIndex; i++) {
    weeksList.value[0][i] = {value: '', label: '', dayIndex: i}
  }

  let weekIndex = 0
  dates.forEach((date, index) => {
    if (date.dayIndex === 0 && index !== 0) {
      weekIndex++
    }
    if (!weeksList.value[weekIndex]) {
      weeksList.value[weekIndex] = []
    }
    weeksList.value[weekIndex][date.dayIndex] = date
  })
}

function onClick(date: any) {
  emit('date-click', date)
}

function processDates() {
  weeksList.value.forEach((week) => {
    week.forEach((date: any) => {
      processDate(date)
    })
  })
}

function checkIsAvailable(date: any) {
  if (props.filterDates) {
    return !!datesList.value.find((day: any) => day.date  === date)
  } else {
    return true
  }
}

fillDates()
fillWeeks()

onUpdated(() => {
  processDates()
})

</script>

<style lang="scss" scoped>
.calendar-month {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  .calendar-month__header {
    text-align: center;
    font-size: 16px;
    line-height: 44px;
    font-weight: 600;
    height: 44px;
  }
  .calendar-month__days {
    display: flex;
    width: 100%;
    max-width: 336px;
    font-size: 14px;
    margin-top: 8px;

    .calendar-month__days-label {
      width: 48px;
      text-align: center;
    }
  }
  .calendar-month__week {
    display: flex;
    width: 100%;
    max-width: 336px;

    &--first {
      margin-top: 10px;
    }
    .week-day {
      text-align: center;
      width: 48px;
      height: 48px;
      button {
        text-align: center;
        background: transparent;
        border: none;
        width: 48px;
        height: 48px;
        border-radius: 24px;
        color: var(--black);
        font-size: 14px;
        line-height: 20px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        user-select: none;

        &:disabled {
          cursor: not-allowed;
        }
        .price {
          font-size: 10px;
          line-height: 12px;
          color: var(--dark-grey);
        }
      }
      &.today button {
        color: var(--blue);
      }
      &.highlighted {
        background-color: var(--blue-highlight);
        button {
          color: var(--black);
        }
        &.start-date {
          border-top-left-radius: 50%;
          border-bottom-left-radius: 50%;
        }
        &.end-date {
          border-top-right-radius: 50%;
          border-bottom-right-radius: 50%;
        }
      }
      &.selected button {
        background-color: var(--blue);
        color: var(--white);
        .price {
          color: var(--whte);
        }
      }
      &.disabled {
        button {
          color: var(--grey);
        }
      }
    }
  }
}
</style>
